"use client";

import { useQuery } from "convex/react";
import { useNavigate } from "react-router";
import { api } from "@convex/_generated/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/format";
import { format, parseISO } from "date-fns";
import { Fuel, Car, ParkingCircle, MoreHorizontal, Receipt } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ExpenseFormSheet } from "./expense-form-sheet";

const TYPE_ICONS = {
  fuel: Fuel,
  tolls: Car,
  parking: ParkingCircle,
  other: MoreHorizontal,
};

export function ExpenseList() {
  const navigate = useNavigate();
  const expenses = useQuery(api.expenses.getExpenseHistory, { limit: 50 });

  if (expenses === undefined) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
      </div>
    );
  }

  if (expenses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Receipt className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="font-semibold">No Expenses Yet</h3>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          Track fuel, tolls, parking, and other costs.
        </p>
        <ExpenseFormSheet />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <ExpenseFormSheet />
      </div>
      {expenses.map((expense) => {
        const Icon = TYPE_ICONS[expense.type];
        // Calculate rewards for fuel expenses
        const flexReward = expense.type === "fuel" && expense.rewardsPercent
          ? Math.round(expense.amount * expense.rewardsPercent / 100)
          : 0;
        const upsideReward = expense.type === "fuel" && expense.gallons && expense.upsidePerGallon
          ? Math.round(expense.gallons * expense.upsidePerGallon)
          : 0;
        const totalReward = flexReward + upsideReward;
        const netAmount = expense.amount - totalReward;

        return (
          <Card
            key={expense._id}
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => navigate(`/expenses/${expense._id}`)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium capitalize">{expense.type}</p>
                    <p className="text-sm text-muted-foreground">
                      {format(parseISO(expense.date), "MMM d")}
                      {expense.type === "fuel" && expense.gallons && (
                        <> · {expense.gallons.toFixed(3)} gal</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {totalReward > 0 ? (
                    <>
                      <p className="text-xs text-muted-foreground line-through">
                        {formatCurrency(expense.amount)}
                      </p>
                      <p className="font-semibold text-destructive">
                        -{formatCurrency(netAmount)}
                      </p>
                      <div className="text-xs text-green-600 space-y-0.5">
                        {flexReward > 0 && (
                          <p>Flex {expense.rewardsPercent}%: +{formatCurrency(flexReward)}</p>
                        )}
                        {upsideReward > 0 && (
                          <p>Upside: +{formatCurrency(upsideReward)}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="font-semibold text-destructive">
                      -{formatCurrency(expense.amount)}
                    </p>
                  )}
                  {expense.blockId ? (
                    <Badge variant="outline" className="text-xs mt-1">Block</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs mt-1">General</Badge>
                  )}
                </div>
              </div>
              {expense.description && (
                <p className="text-sm text-muted-foreground mt-2 pl-8">
                  {expense.description}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
